import { SlideImage, NewsItem, ApiResponse, PaginatedResponse, LoginRequest, LoginResponse } from '../types';

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // If running on Cloudflare Pages (production)
  if (window.location.hostname.includes('pages.dev') || window.location.hostname.includes('workers.dev')) {
    return 'https://stella-haven-admin-api.hildrstella.workers.dev/api';
  }
  
  // Use environment variable if set
  if (process.env.REACT_APP_API_URL) {
    return `${process.env.REACT_APP_API_URL}/api`;
  }
  
  // Local development fallback
  return 'http://localhost:3001/api';
};

const API_BASE_URL = getApiBaseUrl();

// Debug logging
console.log('API Configuration:', {
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  API_BASE_URL,
  NODE_ENV: process.env.NODE_ENV
});

class ApiService {
  private authToken: string | null = null;

  // スライド画像関連
  async getSlideImages(): Promise<ApiResponse<SlideImage[]>> {
    return this.fetchWithErrorHandling<ApiResponse<SlideImage[]>>('/slides');
  }

  async createSlideImage(data: Omit<SlideImage, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<SlideImage>> {
    return this.fetchWithErrorHandling<ApiResponse<SlideImage>>('/slides', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSlideImage(id: string, data: Partial<SlideImage>): Promise<ApiResponse<SlideImage>> {
    return this.fetchWithErrorHandling<ApiResponse<SlideImage>>(`/slides/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSlideImage(id: string): Promise<ApiResponse<void>> {
    return this.fetchWithErrorHandling<ApiResponse<void>>(`/slides/${id}`, {
      method: 'DELETE',
    });
  }

  // お知らせ関連
  async getNewsItems(params?: {
    page?: number;
    limit?: number;
    category?: string;
    published?: boolean;
  }): Promise<ApiResponse<PaginatedResponse<NewsItem>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.category) searchParams.append('category', params.category);
    if (params?.published !== undefined) searchParams.append('published', params.published.toString());

    const query = searchParams.toString();
    const endpoint = query ? `/news?${query}` : '/news';

    return this.fetchWithErrorHandling<ApiResponse<PaginatedResponse<NewsItem>>>(endpoint);
  }

  async getNewsItem(id: string): Promise<ApiResponse<NewsItem>> {
    return this.fetchWithErrorHandling<ApiResponse<NewsItem>>(`/news/${id}`);
  }

  async createNewsItem(data: Omit<NewsItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<NewsItem>> {
    return this.fetchWithErrorHandling<ApiResponse<NewsItem>>('/news', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNewsItem(id: string, data: Partial<NewsItem>): Promise<ApiResponse<NewsItem>> {
    return this.fetchWithErrorHandling<ApiResponse<NewsItem>>(`/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteNewsItem(id: string): Promise<ApiResponse<void>> {
    return this.fetchWithErrorHandling<ApiResponse<void>>(`/news/${id}`, {
      method: 'DELETE',
    });
  }

  // 認証関連
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return this.fetchWithErrorHandling<ApiResponse<LoginResponse>>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async logout(): Promise<ApiResponse<void>> {
    return this.fetchWithErrorHandling<ApiResponse<void>>('/auth/logout', {
      method: 'POST',
    });
  }

  async verifyToken(token: string): Promise<ApiResponse<LoginResponse>> {
    return this.fetchWithErrorHandling<ApiResponse<LoginResponse>>('/auth/verify', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  setAuthToken(token: string) {
    this.authToken = token;
  }

  private async fetchWithErrorHandling<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...options?.headers,
      };

      if (this.authToken) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${this.authToken}`;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers,
        ...options,
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }
}

export const apiService = new ApiService();